import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { userApiService } from "../../../service/admin/userApiService";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { FaSearch, FaSync, FaHistory } from "react-icons/fa";
import { FiEye } from "react-icons/fi";
import CreditModal from "../userManagement/CreditModal";
import usePermissions from "../../../hooks/usePermissions";
import ExcelTable from "../../../component/library/excelTable";
import { getAuthLocalStorage } from "../../../storage/localStorage";
import "./style.css";

const CreditManagement = () => {
  const [users, setUsers] = useState([]);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [searchData, setSearchData] = useState({ search: "" });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [expandedHistory, setExpandedHistory] = useState({});

  const message = useMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const { canEdit } = usePermissions();

  const authData = getAuthLocalStorage();
  const currentUserId = authData?.loginHeader || null;

  // Check if email filter is passed from navigation (e.g., from Credit Modal)
  useEffect(() => {
    if (location.state?.email) {
      setSearchData({ search: location.state.email });
      setPage(1);
      // Clear the state to avoid re-triggering on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleEmailClick = (email, userId, e) => {
    e.stopPropagation();
    if (email) {
      // Navigate to user management list page with email filter (not edit page)
      navigate("/admin/user-management", {
        state: { searchEmail: email },
      });
    }
  };

  const handleViewTransactionHistory = (email, e) => {
    e.stopPropagation();
    if (email) {
      // Navigate to transactions page with email filter
      navigate("/admin/transactions", {
        state: { email: email },
      });
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, limit, searchData.search]);

  const query = `?search=${searchData?.search}&page=${page}&limit=${limit}`;

  const fetchUsers = () => {
    setLoading(true);
    userApiService
      .getAllUsersWithCredit(query)
      .then((res) => {
        if (res.status.code === 200) {
          setUsers(res.data.users || []);
          setPages({
            currentPage: res.data.currentPage,
            totalPages: res.data.totalPages,
            totalItems: res.data.totalItems,
          });
        } else {
          message.showToast(res.error?.message || "Failed to fetch users", ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error", ToastTypes.ERROR);
      })
      .finally(() => {
        setLoading(false);
        setInitialLoading(false);
      });
  };

  const toggleHistory = (userId) => {
    setExpandedHistory((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const handleSelectionChange = (selected) => {
    setSelectedUsers(selected);
  };

  const tableData = useMemo(() => users || [], [users]);

  const columns = [
    {
      key: "uniqueId",
      label: "User ID",
      width: 100,
      render: (value) => <span className="cam-serial-cell">{value ?? "-"}</span>,
    },
    {
      key: "userName",
      label: "Name",
      width: 200,
      render: (value, row) => (
        <div className="cam-user-cell">
          <div className="cam-user-avatar">
            {(value?.[0] || "U").toUpperCase()}
          </div>
          <div className="cam-user-details">
            <span className="cam-user-name">{value || "Unknown"}</span>
          </div>
        </div>
      ),
    },
    {
      key: "email",
      label: "Email",
      width: 220,
      render: (value, row) => (
        <span 
          className="cam-email-cell" 
          style={{ cursor: "pointer", color: "#0969da" }}
          onClick={(e) => handleEmailClick(value, row.userId, e)}
          title="Click to view user details"
        >
          {value || "-"}
        </span>
      ),
    },
    {
      key: "phone",
      label: "Phone",
      width: 130,
      render: (value) => value || "-",
    },
    {
      key: "balance",
      label: "Wallet Balance",
      width: 140,
      render: (value) => (
        <span className="cam-amount">₹{(value || 0).toLocaleString()}</span>
      ),
    },
    {
      key: "created_amount",
      label: "Credit Amount",
      width: 150,
      render: (value) => (
        <span className="cam-created-amount">
          ₹{(value || 0).toLocaleString()}
        </span>
      ),
    },
    {
      key: "historyCount",
      label: "History Count",
      width: 120,
      render: (value) => (
        <span className="cam-history-count">{value || 0} changes</span>
      ),
    },
    {
      key: "lastUpdate",
      label: "Last Update",
      width: 140,
      render: (value) =>
        value ? (
          <div className="cam-date-cell">
            <span>{new Date(value).toLocaleDateString("en-IN")}</span>
            <span className="cam-date-time">
              {new Date(value).toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
        ) : (
          "-"
        ),
    },
    {
      key: "actions",
      label: "Actions",
      width: 150,
      render: (_, row) => (
        <div className="cam-actions-cell">
          {canEdit("user-management") && (
            <>
              <CreditModal
                user={{
                  _id: row.userId,
                  firstName: row.userName?.split(" ")[0] || "",
                  lastName: row.userName?.split(" ").slice(1).join(" ") || "",
                  email: row.email,
                  walletBalance: row.balance,
                }}
                onUpdated={fetchUsers}
              />
              <button
                className="cam-action-btn cam-action-history"
                onClick={(e) => handleViewTransactionHistory(row.email, e)}
                title="View Transaction History"
                style={{ marginLeft: "4px" }}
              >
                <FiEye />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, pages.totalItems);

  const toolbarLeftActions = (
    <div className="cam-toolbar-left">
      <div className="cam-search-box">
        <FaSearch className="cam-search-icon" />
        <input
          type="text"
          className="cam-search-input"
          placeholder="Search by email, phone..."
          value={searchData.search}
          onChange={(e) => setSearchData({ search: e.target.value })}
          onKeyUp={(e) => e.key === "Enter" && fetchUsers()}
        />
      </div>
      <button className="cam-search-btn" onClick={fetchUsers}>
        <FaSearch /> Search
      </button>
      <div className="cam-total-users">
        Total Users: <span>{pages.totalItems || 0}</span>
      </div>
    </div>
  );

  const toolbarRightActions = (
    <div className="cam-toolbar-right">
      <button className="cam-refresh-btn" onClick={fetchUsers} title="Refresh">
        <FaSync className={loading ? "cam-spin" : ""} />
      </button>
      <div className="cam-rows-picker">
        <span>Rows:</span>
        <select
          value={limit}
          onChange={(e) => {
            setLimit(Number(e.target.value));
            setPage(1);
          }}
        >
          {[10, 20, 30, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div className="cam-page">
      <div className="cam-header">
        <h1 className="cam-title">Credit Amount Management</h1>
        <p className="cam-subtitle">
          Manage and track credit amounts for users with admin-assigned credit
        </p>
      </div>

      <div className="cam-table-section">
        <ExcelTable
          data={tableData}
          columns={columns}
          isLoading={initialLoading || loading}
          loadingText="Loading credit amounts..."
          showToolbarExport={true}
          showToolbarFilter={false}
          showToolbarColumns={true}
          showToolbarFullscreen={true}
          toolbarLeftActions={toolbarLeftActions}
          toolbarRightActions={toolbarRightActions}
          onSelectionChange={handleSelectionChange}
          selectedData={selectedUsers}
          selectable={false}
          sortable={true}
          resizable={true}
          showToolbar={true}
          title="Credit Amounts"
          userId={currentUserId}
        />

        {users.length > 0 && (
          <div className="cam-pagination">
            <div className="cam-pagination-info">
              Showing {startItem} to {endItem} of {pages.totalItems} users
            </div>
            <div className="cam-pagination-controls">
              <button
                className="cam-page-btn"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                ‹
              </button>
              {Array.from({ length: Math.min(5, pages.totalPages) }, (_, i) => {
                let pageNum;
                if (pages.totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= pages.totalPages - 2)
                  pageNum = pages.totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <button
                    key={pageNum}
                    className={`cam-page-btn ${
                      page === pageNum ? "cam-page-btn-active" : ""
                    }`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              {pages.totalPages > 5 && page < pages.totalPages - 2 && (
                <>
                  <span className="cam-page-dots">...</span>
                  <button
                    className="cam-page-btn"
                    onClick={() => setPage(pages.totalPages)}
                  >
                    {pages.totalPages}
                  </button>
                </>
              )}
              <button
                className="cam-page-btn"
                disabled={page >= pages.totalPages}
                onClick={() => setPage(page + 1)}
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditManagement;

